<template>
  <div class="qr-code-details">
    <div class="qr-content">
    <v-row class="content mx-0 mt-5">
      <v-col align-self="center" class="flex-grow-0 preview-col pr-0 ml-3">
        <div class="qr-preview d-flex flex-column justify-center">
          <div class="qr-preview-ctn">
            <img class="qr" :src="previewUrl()">
            <img v-if="form.image" :style="{backgroundColor: form.background}" class="logo" :src="toDataUrl(form.image)">
          </div>
          <div class="mt-auto mb-2"></div>
          <v-btn @click="download" class="w-100" outlined color="primary">
            <v-icon class="mr-2">mdi-download</v-icon>
            {{$t('download')}}
          </v-btn>
        </div>
      </v-col>
      <v-col>
        <div class="d-flex flex-column color-inputs-ctn ml-3">
          <v-form ref="qrForm">
            <div class="d-flex flex-row align-center">
              <div class="flex-shrink-1">
                <div class="label font-weight-bold pb-1">{{$t('qr_color')}}</div>
                <color-input :color.sync="form.color"></color-input>
              </div>
              <div class="ml-5">
                <div class="label font-weight-bold pb-1">{{$t('background_color')}}</div>
                <color-input :color.sync="form.background"></color-input>
              </div>
            </div>
          </v-form>
          <div class="color-error pt-5 d-flex" v-if="hasContrastIssue">
            <v-icon color="red" class="mr-3">mdi-alert</v-icon>
            <div class="font-weight-bold" v-html="$t('qr_contrast_warning')"></div>
          </div>
          <div class="mt-4">
            <ImageCropper
              class="add-icon-btn"
              :ratio="1"
              :label="$t('upload_logo')"
              input-key="qr-logo"
              @image-selected="onImageSelect"
              :show-select-dialog="false"
            />
            <v-btn @click="deleteLogo" outlined class="mt-3 add-icon-btn" text v-if="form.image">{{$t('remove_logo')}}</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    <div class="mt-5 ml-3 mr-3">
      <div class="pa-3">
        <div class="mb-5">
          <div class="label font-weight-bold pb-1">{{$t('qr_image_format')}}</div>
          <div class="mt-2">
            <span @click="selectFormat(format)" class="pill uppercase font-weight-regular mr-2 mb-2" :class="{ 'selected': format === form.format }" :key="format" v-for="format in formats">
              {{format}}
            </span>
          </div>
        </div>

        <!-- <div class="mb-5" v-show="hasDPI">
          <div class="label font-weight-bold pb-1" >{{$t('choose_dpi')}}:</div>
          <div>
            <span @click="selectDPI(dpi)" class="pill font-weight-bold mr-2" :class="{ 'selected': dpi === form.dpi }" :key="dpi" v-for="dpi in dpis">
              {{dpi}}dpi
            </span>
          </div>
        </div> -->

        <!-- <div class="mb-5">
          <div class="label font-weight-bold pb-1">{{$t('qr_dimensions_mm')}}</div>
          <div class="hint">{{$t('qr_dimensions_hint')}}</div>

          <v-slider
            class="pt-10 px-2"
            v-model="qrSize"
            step="5"
            max="30"
            min="10"
            thumb-label="always"
            ticks
            dense
            hide-details
          ></v-slider>
          <v-row class="ma-0" justify="space-between">
            <div class="size-hint font-weight-bold">
              {{$t('small')}}<br>(10mm)
            </div>
            <div class="size-hint font-weight-bold">
              {{$t('medium')}}<br>(20mm)
            </div>
            <div class="size-hint font-weight-bold">
              {{$t('large')}}<br>(30mm)
            </div>
          </v-row>
        </div> -->

        <div class="mb-5">
          <div class="label font-weight-bold pb-1">{{$t('create_file_name')}}</div>
          <div class="hint">
            {{$t('naming_convention_hint')}}
          </div>
          <div class="mt-2">
            <span @click="toggleFileNameFields(field)" :class="{ 'selected': isFileNameFieldSelected(field)}" class="pill font-weight-regular mr-2 mb-2" :key="`general-field-${field}`" v-for="field in generalFields">{{field}}</span>
            <span @click="toggleFileNameFields(field)" :class="{ 'selected': isFileNameFieldSelected(field)}" class="pill font-weight-regular mr-2 mb-2" :key="`gs1-field-${field}`" v-for="field in gs1Fields">{{field}}</span>
          </div>
          <v-text-field class="mt-3 filename-input" :label="$t('file_name')" hide-details="auto" outlined small ref="filename" dense v-model="form.filename">
            <template v-slot:append>
              <span class="pt-1">
                .{{form.format}}
              </span>
            </template>
          </v-text-field>
          <!-- <div class="hint">{{$t('tags')}}:</div> -->
          <!-- <div>
            <span class="pill font-weight-bold mr-2 mb-2" @click="toggleTag(field)" :class="{ 'selected': isTagSelected(field)}" :key="field.key" v-for="field in activeScmFields">{{ field.key === 'scantrust_upload_batch' ? $t('upload_batch') : field.key }}</span>
          </div> -->
        </div>
      </div>
    </div>
    </div>
    <div class="actions-ctn">
      <v-btn @click="download" class="mr-3" outlined color="primary">
        <v-icon class="mr-2">mdi-download</v-icon>
        {{$t('download')}}
      </v-btn>
      <v-btn :loading="loading" class="save-btn" @click="save()" color="primary" raised>{{ $t('save') }}</v-btn>
    </div>

  </div>
</template>

<script>

import Api from '@/api'
import { mapGetters, mapMutations } from 'vuex'
import ImageCropper from '@/components/utils/ImageCropper'
import ColorInput from '@/components/utils/ColorInput'
import QRService from '@/services/qr'
import amplitude from 'amplitude-js'
import Utils from '@/utils/utils'

export default {
  name: 'ProductDetailsQr',
  props: ['product'],
  computed: {
    ...mapGetters(['systemPrefix', 'selectedProduct'])
  },
  components: {
    ImageCropper,
    ColorInput
  },
  data () {
    return {
      formats: ['jpg', 'png', 'tiff', 'svg', 'eps', 'pdf'],
      generalFields: ['sku', 'product_name', 'code_id'],
      gs1Fields: ['gtin', 'cpv', 'lot', 'serial'],
      // dpis: [300, 600, 800, 1200, 2400],
      form: {
        dpi: 300,
        format: 'jpg',
        filename: '{code_id}',
        background: '#FFFFFF',
        color: '#000000',
        scale: 10
      },
      initialForm: {},
      hasContrastIssue: false,
      loading: false
    }
  },
  watch: {
    form: {
      deep: true,
      handler (newVal) {
        this.setUnsavedChangesStatus(!Utils.deepCompare(newVal, this.initialForm))
      }
    }
  },
  methods: {
    ...mapMutations({
      setUnsavedChangesStatus: 'SET_UNSAVED_CHANGES_STATUS'
    }),
    onDiscardUnsavedChanges () {
      this.form = JSON.parse(JSON.stringify(this.initialForm))
    },
    isTag (string) {
      return this.gs1Fields.find((field) => {
        return field === string
      })
    },

    // toggleTag (tag) {
    //   if (this.isTagSelected(tag)) {
    //     this.form.filename = this.form.filename.replace(`{${tag.key}}`, '')
    //   } else {
    //     this.form.filename += `{${tag.key}}`
    //   }
    //   this.$refs.filename.focus()
    // },
    // isTagSelected (tag) {
    //   return ~this.form.filename.indexOf(`{${tag.key}}`)
    // },
    selectFormat (format) {
      this.form.format = format
    },

    toggleFileNameFields (field) {
      if (this.isFileNameFieldSelected(field)) {
        this.form.filename = this.form.filename.replace(`{${field}}`, '')
      } else {
        this.form.filename += `{${field}}`
      }
      this.$refs.filename.focus()
    },

    isFileNameFieldSelected (field) {
      return ~this.form.filename.indexOf(`{${field}}`)
    },

    generateFileName (code, string) {
      const matches = string.match(/\{(.*?)\}/)

      if (matches) {
        const submatch = matches[1]
        const before = string.substr(0, string.indexOf('{'))
        const after = string.substr(string.indexOf('}') + 1)
        if (this.isTag(submatch)) {
          // If we somehow need to support non-gs1 tags, this will need to be updated
          console.log('SUBMATCHED ->', submatch, code.scm_data[`gs1_${submatch}`])
          return this.generateFileName(code, `${before}${code.scm_data[`gs1_${submatch}`] ? code.scm_data[`gs1_${submatch}`] : ''}${after}`)
        } else {
          if (submatch === 'code_id') {
            return this.generateFileName(code, `${before}${code.message}${code.scm_data.gs1_data_attributes ? `-${code.scm_data.gs1_data_attributes}` : ''}${after}`)
          } else if (submatch === 'product_name') {
            return this.generateFileName(code, `${before}${code.product ? code.product.name : ''}${after}`)
          } else if (submatch === 'sku') {
            return this.generateFileName(code, `${before}${code.product ? code.product.sku : ''}${after}`)
          } else {
            return this.generateFileName(code, `${before}${submatch}${after}`)
          }
        }
      } else {
        return string
      }
    },

    isBase64StringSVG (base64String) {
      // Decode the base64 string to binary data
      const decodedData = atob(base64String)

      // Check if the decoded data starts with an SVG XML declaration and contains an <svg> element
      return decodedData.startsWith('<?xml version=') && decodedData.includes('<svg')
    },

    toDataUrl (base64) {
      if (base64) {
        if (base64.indexOf('data:image') === 0) {
          return base64
        } else if (!this.isBase64StringSVG(base64)) {
          return `data:image/png;base64,${base64}`
        } else {
          return `data:image/svg+xml;base64,${base64}`
        }
      }
    },

    hexToRgb (hex) {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
      hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b
      })

      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null
    },

    calcLuminance (r, g, b) {
      var a = [r, g, b].map((v) => {
        v /= 255
        return v <= 0.03928
          ? v / 12.92
          : Math.pow((v + 0.055) / 1.055, 2.4)
      })
      return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
    },

    checkContrast () {
      const bgRbg = this.hexToRgb(this.form.background)
      const qrRbg = this.hexToRgb(this.form.color)
      const backgroundLuminance = this.calcLuminance(bgRbg.r, bgRbg.g, bgRbg.b)
      const qrLuminance = this.calcLuminance(qrRbg.r, qrRbg.g, qrRbg.b)

      const ratio = backgroundLuminance > qrLuminance
        ? ((backgroundLuminance + 0.05) / (qrLuminance + 0.05))
        : ((qrLuminance + 0.05) / (backgroundLuminance + 0.05))

      this.hasContrastIssue = ratio < 3.8
    },

    previewUrl () {
      if (this.selectedProduct.code) {
        const gs1DataAttributes = this.selectedProduct.code.scm_data.gs1_data_attributes ? encodeURIComponent(`?${this.selectedProduct.code.scm_data.gs1_data_attributes}`) : ''
        return `${Api.qr_service_url}qr/single/?error_correction=q&color=%23${this.form.color.split('#')[1]}&background=%23${this.form.background.split('#')[1]}&content=${this.systemPrefix}${this.selectedProduct.code.message}${gs1DataAttributes}`
      }
      return ''
    },

    deleteLogo () {
      this.form.image = ''
    },

    onImageSelect (image) {
      this.form = Object.assign({}, this.form, { image: image })
    },

    async save () {
      if (this.$refs.qrForm && !this.$refs.qrForm.validate()) {
        return
      }
      // Check if template is already selected
      this.loading = true
      if (this.selectedProduct.stc_data?.scantrust_qr_template) {
        const image = this.form.image && this.form.image.indexOf(';base64,') ? this.form.image.split(';base64,')[1] : this.form.image
        if (this.form.name !== undefined && this.form.name.trim() === '') {
          delete this.form.name
        }
        await this.$store.dispatch('updateQrTemplate', { id: this.selectedProduct.stc_data.scantrust_qr_template, template: { ...this.form, image: image } })
        this.setUnsavedChangesStatus(false)
        this.loading = false
      } else {
        const res = await this.$store.dispatch('createQrTemplate', this.form)
        await this.$store.dispatch('updateProductJson', { id: this.selectedProduct.id, json: { scantrust_qr_template: res.id } })
        this.setUnsavedChangesStatus(false)
        this.loading = false
      }
      this.initialForm = JSON.parse(JSON.stringify(this.form))
      this.$store.dispatch('showSnackBar', { message: this.$t('saved_successfully') })
    },

    async download () {
      const codesArray = []
      const image = this.form.image && this.form.image.indexOf('data:image/svg+xml;base64,') === 0 ? this.form.image.split('data:image/svg+xml;base64,')[1] : this.form.image

      const generateQrZip = async () => {
        const params = {
          scale: this.form.scale,
          codes: codesArray,
          format: this.form.format,
          dpi: this.form.dpi,
          color: this.form.color,
          background: this.form.background,
          image: image
        }
        await QRService.generateQrZip(params)
      }

      amplitude.getInstance().logEvent('QR Code Downloaded', { file_format: this.form.format, qr_type: 'product', resolution: this.form.dpi })
      const filename = this.generateFileName(this.selectedProduct.code, this.form.filename).replace(/\//g, '-')
      codesArray.push({ content: `${this.systemPrefix}${this.selectedProduct.code.message}${this.selectedProduct.code.scm_data.gs1_data_attributes ? `?${this.selectedProduct.code.scm_data.gs1_data_attributes}` : ''}`, filename: `${filename}.${this.form.format}` })
      await generateQrZip()
      // const scale = this.milimitersToScale(this.qrSize, this.form.dpi, `${this.systemPrefix}${code.message}`)
      // if (scale > this.form.scale) {
      //   this.form.scale = scale
      // }
    }
  },
  async created () {
    if (this.selectedProduct.stc_data?.scantrust_qr_template) {
      const res = await this.$store.dispatch('loadQrTemplate', { id: this.selectedProduct.stc_data?.scantrust_qr_template, product: this.selectedProduct.id })
      this.form = res.template
    }
    this.initialForm = JSON.parse(JSON.stringify(this.form))
  },
  mounted () {
    window.addEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  },
  beforeDestroy () {
    window.removeEventListener('discard-unsaved-changes', this.onDiscardUnsavedChanges)
  }
}

</script>

<style lang="sass" scoped>

@import "@/variables.sass"

.color-inputs-ctn
  height: 100%

.qr-preview
  width: 186px
  height: 100%
  text-align: center
  img
    margin-left: auto
    margin-right: auto
  .qr-preview-ctn
    margin-left: auto
    margin-right: auto
    position: relative
    width: 186px
    .qr
      width: 186px
    .logo
      position: absolute
      width: 54px
      padding: 6px
      top: calc(50% - (54px / 2))
      left: calc(50% - (54px / 2))
      background: white
      &:not(.left).right
        left: calc(50% - 40px)
      &:not(.right).left
        left: calc(50% - 18px)
      &:not(.bottom).top
        top: calc(50% - 18px)
      &:not(.top).bottom
        top: calc(50% - 38px)

.pill
  display: inline-block
  text-align: center
  color: $primary
  border: 1px solid $primary
  border-radius: 5px
  padding: 6px 8px
  cursor: pointer
  &.selected
    color: white
    background: $primary

.uppercase
  text-transform: uppercase

.qr-code-details
  display: flex
  flex-direction: column
  height: 100%
  min-height: 0

.qr-content
  flex: 1 1 auto
  overflow-y: auto
  padding-bottom: 8px
  min-height: 0

.actions-ctn
  display: flex
  direction: row
  position: sticky
  bottom: 0
  background: white
  padding: 20px
  text-align: left
  border-top: 1px solid #eee
  z-index: 5

.save-btn
  margin: 0
  min-width: 140px

.add-icon-btn
  min-width: 200px !important
  max-width: 200px !important

.filename-input
  max-width: 600px

@media (max-width: 960px)
  .actions-ctn
    position: fixed
    left: 0
    right: 0
    bottom: 0
    padding: 12px 16px
    border-top: 1px solid #eee
    z-index: 20
  .qr-content
    padding-bottom: 80px

</style>
